import { useCallback, useEffect, useState } from "react";
import { Database, Loader2, Play, Upload } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const LABELS = {
  records: "Records in file", already_present: "Already in this database", would_insert: "Would be added",
  duplicates_skipped: "Duplicates skipped (same team + name)", would_replace: "Would be replaced", protected: "Protected, kept as is",
  live_only: "Live-only players, kept", teams: "Teams",
  players_in_db_for_sport: "Players of this sport now", found_by_id: "Found by _id", found_by_external_id: "Found by FanFlare ID",
  not_found: "Not found", would_update: "Would be updated", with_tags: "With ≥1 tag", inserted: "Added", replaced: "Replaced", teams_upserted: "Teams saved", updated: "Updated",
};

const Result = ({ r, testId }) => (
  <div data-testid={testId} className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-800 bg-slate-800 sm:grid-cols-4">
    {Object.entries(LABELS).filter(([k]) => k in r).map(([k, l]) => (
      <div key={k} className="bg-[#131927] p-3">
        <div className="eyebrow text-[10px]">{l}</div>
        <div data-testid={`${testId}-${k}`} className="mt-1 font-data text-lg font-bold text-white">{Number(r[k]).toLocaleString()}</div>
      </div>
    ))}
  </div>
);

const Missing = ({ list, total, what }) => !total ? null : (
  <details data-testid="transfer-missing-list" className="mt-3 text-xs text-slate-400">
    <summary className="cursor-pointer">Show {what}{total > list.length ? ` (first ${list.length} of ${total})` : ""}</summary>
    <div className="mt-2 max-h-48 overflow-y-auto font-data">{list.map((m) => <div key={m._id}>{m._id} · {m.name} · {m.school}</div>)}</div>
  </details>
);

const DatasetCard = ({ d, onDone }) => {
  const [busy, setBusy] = useState(null);
  const [dry, setDry] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [replace, setReplace] = useState(false);
  const toggleReplace = (v) => { setReplace(v); setDry(null); };
  const go = async (mode) => {
    setBusy(mode);
    try {
      const r = (await api.post(`/admin/transfers/${d.key}/${mode}${replace ? "?replace=true" : ""}`)).data;
      if (mode === "dry-run") setDry(r); else { setDry(null); toast.success(`${d.label} written`); }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };
  return (
    <div data-testid={`transfer-card-${d.key}`} className="rounded-xl border border-slate-800 bg-[#0F1522] p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-white"><Database className="h-4 w-4 text-[#FF2D78]" />{d.label}</div>
          <div className="mt-1 font-data text-[11px] text-slate-500">
            {d.last_apply ? `Last written ${fmtDate(d.last_apply.at)}` : "Never written to this database"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {d.kind === "rosters" && (
            <label className="flex items-center gap-2 text-xs text-slate-300" title="Removes live players that are the same team + name as a preview player but have a different _id, unless they have admin edits or attributes">
              <Switch data-testid={`transfer-replace-toggle-${d.key}`} checked={replace} onCheckedChange={toggleReplace} />
              Replace live-scraped copies
            </label>
          )}
          <button data-testid={`transfer-dry-run-${d.key}`} onClick={() => go("dry-run")} disabled={!!busy}
            className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm hover:border-white disabled:opacity-50">
            {busy === "dry-run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Dry run
          </button>
          <button data-testid={`transfer-apply-${d.key}`} onClick={() => setConfirm(true)} disabled={!dry || !!busy}
            className="inline-flex items-center gap-2 rounded-full bg-[#FF2D78] px-4 py-2 text-sm font-semibold text-white hover:bg-[#E01F66] disabled:opacity-40">
            {busy === "apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Write
          </button>
        </div>
      </div>
      {dry && <><Result r={dry} testId={`transfer-dry-result-${d.key}`} /><Missing list={dry.missing || []} total={dry.not_found ?? dry.protected ?? dry.duplicates_skipped}
        what={d.kind !== "rosters" ? "not-found records" : replace ? "protected players" : "skipped duplicates"} /></>}
      {!dry && d.last_apply && <Result r={d.last_apply} testId={`transfer-last-apply-${d.key}`} />}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-slate-800 bg-[#131927]">
          <AlertDialogHeader>
            <AlertDialogTitle>Write {d.label}?</AlertDialogTitle>
            <AlertDialogDescription data-testid={`transfer-confirm-text-${d.key}`}>
              {replace && dry?.would_replace ? `${dry.would_replace.toLocaleString()} live-scraped players will be replaced by the preview players (keeping preview _ids). ` : ""}
              This writes to the database this site is connected to. It is safe to run again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`transfer-cancel-${d.key}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`transfer-confirm-${d.key}`} onClick={() => go("apply")} className="bg-[#FF2D78] hover:bg-[#E01F66]">Write</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const DataTransferTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/transfers").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-sm text-slate-500">Loading datasets</p>;
  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-slate-400">
        Data packaged from the preview database. Run each dry run first, then write. Do rosters before attributes, because attributes match players by _id.
        Volleyball attributes fall back to the FanFlare player ID when the _id differs. If the live site already scraped a sport, turn on
        "Replace live-scraped copies" so those players are swapped for the preview players and keep the _ids the attribute files use.
      </p>
      {data.datasets.map((d) => <DatasetCard key={d.key} d={d} onDone={load} />)}
    </div>
  );
};
